<script lang="ts">
    import { KEY_CODE_ARROW_LEFT, KEY_CODE_ARROW_RIGHT } from "@/constants";
    import InstagramIcon from "@apps/instagram/components/icons/InstagramIcon.svelte";
    import ArrowIcon from "@apps/instagram/widgets/instagram_gallery_widget/components/icons/ArrowIcon.svelte";
    import { createEventDispatcher, tick } from "svelte";

    import BaseButton from "@components/Buttons/BaseButton.svelte";
    import GrowaveBrand from "@components/GrowaveBrand/GrowaveBrand.svelte";
    import Icon from "@components/Icon.svelte";
    import CloseIcon from "@components/icons/CloseIcon.svelte";
    import Scrollable from "@components/Scrollable.svelte";
    import T from "@components/T.svelte";

    import InstagramPostModalManager from "./InstagramPostModalManager";
    import InstagramPostModalTagsList from "./InstagramPostModalTagsList.svelte";
    import InstagramPostModalViewer from "./InstagramPostModalViewer.svelte";

    export let instagramPostModalManager: InstagramPostModalManager;
    export let highQualityEnabled: boolean;
    export let productQuickViewEnabled: boolean;
    export let hideBranding: boolean;
    const dispatch = createEventDispatcher();
    const activePost$ = instagramPostModalManager.activePost$;
    const selectedTagId$ = instagramPostModalManager.selectedTagId$;
    const activeMediaTags$ = instagramPostModalManager.activeMediaTags$;
    let modalElement: null | HTMLDivElement = null;
    const handleNext = () => {
        instagramPostModalManager.next();
    };
    const handlePrev = () => {
        instagramPostModalManager.prev();
    };
    const handleKeydown = (e: KeyboardEvent) => {
        if (e.keyCode === KEY_CODE_ARROW_LEFT) {
            handlePrev();
        } else if (e.keyCode === KEY_CODE_ARROW_RIGHT) {
            handleNext();
        }
    };
    const handleTagSelect = (e: CustomEvent) => {
        instagramPostModalManager.selectTag(e.detail);
    };
    const handleTagUnSelect = () => {
        instagramPostModalManager.unSelectTag();
    };
    const handleProductTagOpen = (e: CustomEvent<number>) => {
        instagramPostModalManager.openProductTag(e.detail);
    };
    const handleClose = () => {
        dispatch("close");
    };
    const setFocus = async () => {
        await tick();
        modalElement?.focus();
    };
    $: {
        $activePost$;
        if ($activePost$) {
            setFocus();
        }
    }
</script>

<div
    tabindex="-1"
    on:keydown={handleKeydown}
    bind:this={modalElement}
    class="gw-ig-post-modal-post-view"
>
    {#if $activePost$}
        <div class="gw-ig-post-modal-post-view__left">
            {#key $activePost$}
                <InstagramPostModalViewer
                    on:tag_select={handleTagSelect}
                    on:tag_unselect={handleTagUnSelect}
                    post={$activePost$}
                    activeMediaTags={$activeMediaTags$}
                    selectedTagId={$selectedTagId$}
                    {instagramPostModalManager}
                    {highQualityEnabled}
                />
            {/key}
        </div>
        <div class="gw-ig-post-modal-post-view__right">
            <div class="gw-ig-post-modal-post-view__header">
                <a
                    href={$activePost$.permalink}
                    class="gw-ig-post-modal-post-view__ig-link gw-link-reset-default-styles"
                    rel="noopener noreferrer"
                    target="_blank"
                    aria-label="Opens post on instagram in a new tab"
                >
                    <Icon
                        width={18}
                        height={18}
                        class="gw-ig-post-modal-post-view__ig-link-icon"
                    >
                        <InstagramIcon />
                    </Icon>
                    <span class="gw-ig-post-modal-post-view__ig-link-text">
                        <T
                            namespace="instagram.critical.InstagramGalleryWidget"
                            translationKey="view_on_instagram"
                        />
                    </span>
                </a>
                <BaseButton
                    class="gw-ig-post-modal-post-view__close"
                    ariaLabel="Close post modal"
                    on:click={handleClose}
                >
                    <Icon width={12} height={12}>
                        <CloseIcon />
                    </Icon>
                </BaseButton>
            </div>
            <Scrollable class="gw-ig-post-modal-post-view__right-scrolle">
                <div class="gw-ig-post-modal-post-view__right-content">
                    <InstagramPostModalTagsList
                        on:tag_select={handleTagSelect}
                        on:tag_unselect={handleTagUnSelect}
                        on:tag_open={handleProductTagOpen}
                        selectedTagId={$selectedTagId$}
                        activeMediaTags={$activeMediaTags$}
                        activePostId={$activePost$.id}
                        {productQuickViewEnabled}
                    />
                    {#if $activePost$.caption}
                        <div class="gw-ig-post-modal-post-view__desc">
                            {@html $activePost$.caption}
                        </div>
                    {/if}
                    <GrowaveBrand
                        class="gw-ig-post-modal-post-view__growave-brand"
                        canShowWatermark={!hideBranding}
                    />
                </div>
            </Scrollable>
            <div class="gw-ig-post-modal-post-view__footer">
                <BaseButton
                    ariaLabel="Go to the previous image"
                    class="gw-ig-post-modal-btn--prev"
                    on:click={handlePrev}
                >
                    <span
                        class="gw-ig-post-modal-product-view__button-icon gw-ig-post-modal-product-view__button-icon--left"
                    >
                        <Icon width={15} height={15}>
                            <ArrowIcon />
                        </Icon>
                    </span>
                    <T
                        namespace="instagram.critical.InstagramGalleryWidget"
                        translationKey="prev"
                    />
                </BaseButton>
                <BaseButton
                    ariaLabel="Go to the next image"
                    class="gw-ig-post-modal-btn--next"
                    on:click={handleNext}
                >
                    <T
                        namespace="instagram.critical.InstagramGalleryWidget"
                        translationKey="next"
                    />
                    <span
                        class="gw-ig-post-modal-product-view__button-icon gw-ig-post-modal-product-view__button-next"
                    >
                        <Icon width={15} height={15}>
                            <ArrowIcon />
                        </Icon>
                    </span>
                </BaseButton>
            </div>
        </div>
    {/if}
</div>

<style lang="postcss">
    .gw-ig-post-modal-post-view {
        display: flex;
        height: 100%;
        outline: none;
        outline-offset: unset;
        box-shadow: unset;

        :global(&__growave-brand) {
            text-align: center;
        }
    }
    :global(.gw-ig-post-modal-btn--prev, .gw-ig-post-modal-btn--next) {
        display: flex;
        align-items: center;
        column-gap: 6px;
        text-transform: capitalize;
        color: var(--gw-post-modal-text-color);
    }
    .gw-ig-post-modal-post-view__left {
        width: 100%;
        overflow: hidden;
        background-color: #000;
    }
    .gw-ig-post-modal-post-view__right {
        display: flex;
        flex-direction: column;
        min-width: 405px;
    }

    .gw-ig-post-modal-post-view__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 2em 0.8em 0.8em;
    }

    .gw-ig-post-modal-post-view__ig-link {
        display: flex;
        align-items: center;
        padding: 8px;
        column-gap: 10px;
        border-radius: var(--gw-border-radius-base);
        background: var(--gw-lightgrey-bg);
        color: #fff;
        font-size: 0.8em;
        line-height: 0;
    }

    .gw-ig-post-modal-post-view__header
        :global(.gw-ig-post-modal-post-view__close) {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        background-color: var(--gw-lightgrey-bg);
        border-radius: 50%;
        color: var(--gw-modal-close-icon-color);
    }

    .gw-ig-post-modal-post-view__right
        :global(.gw-ig-post-modal-post-view__right-scrolle) {
        position: relative;
        height: 100%;
    }
    .gw-ig-post-modal-post-view__right-content {
        position: absolute;
        width: 100%;
        padding: 0 15px;
    }
    .gw-ig-post-modal-post-view__desc {
        margin: 2em 0;
        color: var(--gw-post-modal-text-color);
        font-size: 0.8em;
        white-space: break-spaces;
        word-break: break-all;
    }
    :global(.gw-ig-post-modal-post-view__ig-link-icon) {
        width: 22px;
        height: 22px;
        padding: 2px;
        text-align: center;
        vertical-align: middle;
        background: radial-gradient(
            125.51% 502.1% at 0.14% 201.03%,
            #fec053 0%,
            #f2203e 32.73%,
            #b729a8 64.85%,
            #5342d6 100%
        );
        border-radius: 5px;
    }
    .gw-ig-post-modal-post-view__ig-link-text {
        font-size: var(--gw-font-size-xlg);
        color: var(--gw-post-modal-text-color);
    }
    .gw-ig-post-modal-post-view__footer {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 25px;
        box-shadow: 0px 0px 5px 2px rgba(0, 0, 0, 0.03);
        background-color: var(--gw-footer-bg, none);
    }
    .gw-ig-post-modal-product-view__button-icon--left {
        transform: rotate(180deg);
    }

    @media all and (max-width: 970px) {
        .gw-ig-post-modal-post-view {
            font-size: 0.9em;
        }
    }

    @media all and (max-width: 900px) {
        .gw-ig-post-modal-post-view {
            flex-flow: column;
        }

        .gw-ig-post-modal-post-view__header
            :global(.gw-ig-post-modal-post-view__close) {
            position: absolute;
            top: 24px;
            right: 24px;
            background: var(--gw-grey-close-btn-bg);
            color: var(--gw-grey-reset-button-color);
        }

        .gw-ig-post-modal-post-view__right {
            min-width: unset;
        }
        .gw-ig-post-modal-post-view__right-content {
            position: relative;
        }
    }
    @media all and (max-width: 768px) {
        .gw-ig-post-modal-post-view__footer {
            position: sticky;
            bottom: 0;
            background: var(--gw-white-bg);
            z-index: 99;
        }
    }

    @media all and (max-width: 425px) {
        .gw-ig-post-modal-post-view__header
            :global(.gw-ig-post-modal-post-view__close) {
            right: 16px;
        }
    }
</style>
